/**
 * Serbian formatting.
 *
 * Serbia writes thousands with a dot and decimals with a comma, so "12.500" is twelve and a half
 * thousand. Formatting these by hand is how a price ends up reading as twelve euros to the only
 * audience the site has, which is why everything here goes through Intl with an explicit locale.
 */

const LOCALE = 'sr-Latn-RS';

const priceFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

export function formatPrice(value: number): string {
  return priceFormatter.format(value);
}

export function formatMileage(km: number): string {
  return `${numberFormatter.format(km)} km`;
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatPower(kw?: number | null): string | null {
  if (!kw) {
    return null;
  }

  // Serbian listings quote both, and buyers compare in horsepower while paperwork uses kilowatts.
  return `${kw} kW (${Math.round(kw * 1.35962)} KS)`;
}

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function formatDate(iso: string): string {
  return dateFormatter.format(new Date(iso));
}

/**
 * "prije 3 dana" reads better than a date on a listing card: what a buyer wants to know is whether
 * the ad is fresh, not which Tuesday it appeared.
 */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const minutes = Math.round((Date.now() - then) / 60_000);

  if (minutes < 1) return 'upravo sada';
  if (minutes < 60) return `pre ${minutes} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `pre ${hours} h`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'juče';
  if (days < 7) return `pre ${days} dana`;
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? 'pre nedelju dana' : `pre ${weeks} nedelje`;
  }

  return formatDate(iso);
}

/**
 * Serbian pluralisation has three forms, not two: 1 oglas, 2 oglasa, 5 oglasa. The teens are the
 * trap — 11 takes the many-form despite ending in 1 — which is why this is not a ternary.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;

  return many;
}

export function formatListingCount(count: number): string {
  return `${numberFormatter.format(count)} ${plural(count, 'oglas', 'oglasa', 'oglasa')}`;
}
